import { lazy, ReactNode, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Box } from '@chakra-ui/react'
import { Capacitor } from '@capacitor/core'
import AuthWrapper from '../components/common/layout/AuthWrapper'
import { useAuth } from '../hooks/useAuth'
import IndexPage from '../pages/Index'

// Lazy imports - Public pages (kept: register, password flows)
const Login = lazy(() => import('../pages/auth/Login'))
const Register = lazy(() => import('../pages/auth/Register'))
const ChangePassword = lazy(() => import('../pages/auth/ChangePassword'))
const ChangePasswordSuccess = lazy(() => import('../pages/auth/ChangePasswordSuccess'))
const ShowValue = lazy(() => import('../pages/redirect/ShowValue'))
const MfaSelect = lazy(() => import('../pages/auth/MfaSelect'))
const OtpChallenge = lazy(() => import('../pages/auth/OtpChallenge'))
const OtpEnroll = lazy(() => import('../pages/auth/OtpEnroll'))

export const LoginRedirect = () => {
  const { loginWithUsernameAndPassword, accessDenied } = useAuth()
  useEffect(() => {
    if (sessionStorage.getItem('logging_out')) return
    if (!accessDenied) loginWithUsernameAndPassword('', '')
  }, [accessDenied])
  return null
}

export const PublicAuthRouteWrapper = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth()
  const location = useLocation()

  if (isAuthenticated && location.pathname !== '/auth/change-password') return <Navigate to="/react/dashboard" />
  return (
    <AuthWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </AuthWrapper>
  )
}

export const PublicRedirectRouteWrapper = ({ children }: { children: ReactNode }) => {
  return <Suspense fallback={<Box bg="primary.400" w="100vw" h="100vh" />}>{children}</Suspense>
}

export const PublicRoutes = () => {
  return (
    <>
      <Route
        path="redirect/*"
        element={
          <PublicRedirectRouteWrapper>
            <Routes>
              <Route path="/show-value/:field/:key" element={<ShowValue />} />
            </Routes>
          </PublicRedirectRouteWrapper>
        }
      />
      <Route
        path="auth/*"
        element={
          <Routes>
            {/* These pages still need the in-app UI */}
            {/* Native only: render in-app Login screen. On web /auth/login falls through to Auth0 redirect. */}
            {Capacitor.isNativePlatform() && (
              <Route path="/login" element={<PublicAuthRouteWrapper><Login /></PublicAuthRouteWrapper>} />
            )}
            {Capacitor.isNativePlatform() && (
              <Route path="/mfa-select" element={<PublicAuthRouteWrapper><MfaSelect /></PublicAuthRouteWrapper>} />
            )}
            {Capacitor.isNativePlatform() && (
              <Route path="/otp-challenge" element={<PublicAuthRouteWrapper><OtpChallenge /></PublicAuthRouteWrapper>} />
            )}
            {Capacitor.isNativePlatform() && (
              <Route path="/otp-enroll" element={<PublicAuthRouteWrapper><OtpEnroll /></PublicAuthRouteWrapper>} />
            )}
            <Route path="/register" element={<PublicAuthRouteWrapper><Register /></PublicAuthRouteWrapper>} />
            <Route path="/change-password" element={<PublicAuthRouteWrapper><ChangePassword /></PublicAuthRouteWrapper>} />
            <Route path="/password-success" element={<PublicAuthRouteWrapper><ChangePasswordSuccess /></PublicAuthRouteWrapper>} />
            {/* All other /auth/* paths → redirect to Auth0 via loginWithRedirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        }
      />

      {/* Catch-all route */}
      <Route path="*" element={<IndexPage />} />
    </>
  )
}
